import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { productModules } from '../../data/products';
import { HolderPairIllustration, InsertPairIllustration, ServiceItemsIllustration, StarterKitIllustration } from '../illustrations/TechnicalIllustrations';
function ModuleLink({
  label,
  href
}: {
  label: string;
  href: string;
}) {
  return <Link to={href} data-analytics="product-category-click" data-analytics-label={label} className="mt-auto inline-flex min-h-[44px] items-center gap-2 self-start border-b border-copper pt-8 text-[14px] font-semibold text-navy transition-colors duration-150 ease-precise hover:text-copper" data-id="element-238" data-mp="tvvelrrb070l">
      {label}
      <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-239" data-mp="2eb6u0ffb71ib" />
    </Link>;
}
function ModuleFrame({
  index,
  heading,
  illustration,
  children,
  className = ''
}: {
  index: string;
  heading: string;
  illustration: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return <article className={`relative flex flex-col overflow-hidden border border-rule bg-white p-6 lg:p-9 ${className}`} data-id="element-240" data-mp="1m4b7oor6hd2u">
      <div className="relative flex items-center gap-3 border-b border-rule pb-4 sm:gap-5" data-id="element-241" data-mp="1q77uysrnxxd4">
        <h3 className="min-w-0 flex-1 font-display text-[22px] font-medium leading-tight text-navy lg:text-[26px]" data-id="element-242" data-mp="1v5hsha0v1a1w">
          {heading}
        </h3>
        <div className="w-[72px] shrink-0 overflow-hidden sm:w-[104px] lg:w-[120px]" data-id="element-243" data-mp="ycw3eqfucloy">
          {illustration}
        </div>
        <span className="hidden shrink-0 font-mono text-[10px] tracking-label text-copper sm:block" data-id="element-244" data-mp="17fx8cnsz06r1">
          {index}
        </span>
        <span className="absolute bottom-0 right-0 translate-y-1/2 bg-white pl-1.5 font-mono text-[10px] leading-none tracking-label text-copper sm:hidden" data-id="element-245" data-mp="1c7f672zp5r7s">
          {index}
        </span>
      </div>
      {children}
    </article>;
}
function SpecRow({
  term,
  value,
  emphasis = false
}: {
  term: string;
  value: string;
  emphasis?: boolean;
}) {
  return <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3" data-id="element-246" data-mp="1i25dg9ivfd46">
      <dt className={`font-mono ${emphasis ? 'text-[14px] text-navy' : 'text-[13.5px] text-navy'}`} data-id="element-247" data-mp="1vtahdnt9urkb">
        {term}
      </dt>
      <dd className="rp-tabular font-mono text-[12.5px] tracking-[0.04em] text-graphite/70" data-id="element-248" data-mp="1rs8fez7z01en">
        {value}
      </dd>
    </div>;
}
export function ProductSystem() {
  const [kits, holders, inserts, accessories] = productModules;
  return <section id="products" aria-labelledby="products-heading" className="w-full bg-warm" data-id="element-249" data-mp="1d2ki092wtmw7">
      <div className="mx-auto max-w-shell px-5 py-16 lg:px-10 lg:py-24" data-id="element-250" data-mp="183g81g85vl4t">
        <SectionHeading id="products-heading" eyebrow="PRODUCT SYSTEM" heading="Build the Shear Geometry setup your application needs" intro="Start with the shank size, then review the published roughing, chipbreaker or titanium-oriented insert. These product numbers and configurations come from Robertson Precision’s legacy published catalog. Confirm current availability, compatibility, packaging, pricing and lead time directly before ordering." data-id="element-251" data-mp="2ohz9wyg9hokg" />

        <div className="mt-14 grid gap-5 lg:grid-cols-12" data-id="element-252" data-mp="2n0smg12j5edm">
          {/* A — Starter kits */}
          <ModuleFrame index={kits.index} heading={kits.heading} illustration={<StarterKitIllustration data-id="element-254" data-mp="3fphlzvnxtbn9" />} className="lg:col-span-7" data-id="element-253" data-mp="3aqei4ejp1bgv">
            <p className="relative mt-6 max-w-[52ch] text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-255" data-mp="3fraocyrip9pg">
              {kits.lead}
            </p>
            <dl className="mt-7 grid gap-px bg-rule sm:grid-cols-2" data-id="element-256" data-mp="35ymqe0oswzp5">
              <div className="bg-white p-5" data-id="element-257" data-mp="3tpipzjzi0z6y">
                <dt className="font-mono text-[15px] text-navy" data-id="element-258" data-mp="3tno3404o2x08">SG Kit 1″</dt>
                <dd className="rp-tabular mt-2 font-mono text-[11px] tracking-label text-graphite/70" data-id="element-259" data-mp="2auwo1p9kxcd">
                  1-INCH HOLDER SYSTEM
                </dd>
              </div>
              <div className="bg-white p-5" data-id="element-260" data-mp="25s1fet23pa5m">
                <dt className="font-mono text-[15px] text-navy" data-id="element-261" data-mp="25qzttungl59n">SG Kit 3/4″</dt>
                <dd className="rp-tabular mt-2 font-mono text-[11px] tracking-label text-graphite/70" data-id="element-262" data-mp="20u9bf0o6lxl2">
                  3/4-INCH HOLDER SYSTEM
                </dd>
              </div>
            </dl>
            <p className="mt-6 max-w-[58ch] border-l-2 border-copper pl-4 text-[14px] leading-[1.65] text-graphite/85" data-id="element-263" data-mp="2tgu2e00qw7o3">
              {kits.details[2]}
            </p>
            <ModuleLink label={kits.linkLabel} href={kits.href} data-id="element-264" data-mp="2jrsnkd5xrwbp" />
          </ModuleFrame>

          {/* B — Tool holders */}
          <ModuleFrame index={holders.index} heading={holders.heading} illustration={<HolderPairIllustration data-id="element-266" data-mp="3c9err7h8jun5" />} className="lg:col-span-5" data-id="element-265" data-mp="2es7qk8abty1m">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-267" data-mp="37gvd076tatgr">
              {holders.lead}
            </p>
            <dl className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-268" data-mp="3euvzhezaosut">
              <SpecRow term="SALXR 16-3D" value="1-INCH SHANK" emphasis data-id="element-269" data-mp="3jsxgzvh2ok46" />
              <SpecRow term="SALXR 12-3B" value="3/4-INCH SHANK" emphasis data-id="element-270" data-mp="3oqpj1ibflmm3" />
            </dl>
            <p className="mt-4 text-[14px] leading-[1.6] text-graphite/80" data-id="element-271" data-mp="3tq22ru70adkg">
              Uses low-profile insert screws and a T-15 Torx driver.
            </p>
            <ModuleLink label={holders.linkLabel} href={holders.href} data-id="element-272" data-mp="1kggxnkrv16c" />
          </ModuleFrame>

          {/* C — Inserts & chipbreakers */}
          <ModuleFrame index={inserts.index} heading={inserts.heading} illustration={<InsertPairIllustration data-id="element-274" data-mp="gbp13h5wnuc3" />} className="lg:col-span-8" data-id="element-273" data-mp="bbsxhj14e4x2">
            <p className="mt-6 max-w-[62ch] text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-275" data-mp="lb1z8y288ntz">
              {inserts.lead}
            </p>
            <div className="mt-7 border-y border-rule" data-id="element-276" data-mp="pcn7g63tgdxu">
              <div className="hidden items-baseline justify-between border-b border-rule pb-2 sm:flex" data-id="element-277" data-mp="2mchx0egfawhh">
                <span className="font-mono text-[9.5px] tracking-label text-copper" data-id="element-278" data-mp="2he6oq85s0u1d">
                  PUBLISHED INSERT
                </span>
                <span className="font-mono text-[9.5px] tracking-label text-copper" data-id="element-279" data-mp="2v6mnuxvk47x0">
                  NOSE RADIUS
                </span>
              </div>
              <ul className="divide-y divide-rule" data-id="element-280" data-mp="352pvgj96gyrc">
                {inserts.details.map(sku => {
                const [name, radius] = sku.split(' — ');
                return <li key={sku} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-2.5" data-id="element-281" data-mp="3e3nv6pcl2k44">
                      <span className="font-mono text-[13px] leading-snug text-navy" data-id="element-282" data-mp="1jpwljpu9808o">
                        {name}
                      </span>
                      <span className="rp-tabular font-mono text-[12.5px] text-graphite/70" data-id="element-283" data-mp="1fpcmggq4emjw">
                        {radius}
                      </span>
                    </li>;
              })}
              </ul>
            </div>
            <p className="mt-4 max-w-[62ch] text-[13.5px] leading-[1.6] text-graphite/70" data-id="element-284" data-mp="19nspzga095l3">
              {inserts.note}
            </p>
            <ModuleLink label={inserts.linkLabel} href={inserts.href} data-id="element-285" data-mp="10supno4j2qjq" />
          </ModuleFrame>

          {/* D — Accessories */}
          <ModuleFrame index={accessories.index} heading={accessories.heading} illustration={<ServiceItemsIllustration data-id="element-287" data-mp="qtdn3fkkosrz" />} className="lg:col-span-4" data-id="element-286" data-mp="luu1jg9w5j05">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-288" data-mp="cxjqnw0zjvvr">
              {accessories.lead}
            </p>
            <ul className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-289" data-mp="279q99fw7ley3">
              {accessories.details.map(item => <li key={item} className="py-3.5 font-mono text-[13.5px] text-navy" data-id="element-290" data-mp="2c92trtzly9xi">
                  {item}
                </li>)}
            </ul>
            <ModuleLink label={accessories.linkLabel} href={accessories.href} data-id="element-291" data-mp="3fvgm0l7e5d0w" />
          </ModuleFrame>
        </div>
      </div>
    </section>;
}